import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { HierarchyCountBadge } from './HierarchyCountBadge';
import { FlatHierarchyRow, flattenHierarchy, NodeTotals } from './HierarchyTree';
import { ServiceLocationHierarchy } from '../../../../../@types/service.type';
import { TablePagination } from '../../../../../components/molecules/Pagination';

const ROWS_PER_PAGE = 10;
// Fixed row height, applied to every cell regardless of whether it holds a
// badge or a plain "–" — without this, a badge's own padding/line-height
// makes rows with counts taller than rows without, so the table's overall
// height (and the pagination control sitting below it) shifts per page
// depending on which rows happen to have jobs/parts. The container below
// reserves height for a full page of these, so a shorter last page doesn't
// shrink the block and move the pagination control up.
const ROW_HEIGHT = 32;
// Fixed widths for the two count columns; Name takes whatever's left.
// MUI's <Table> defaults to `table-layout: auto`, which sizes columns from
// whatever content happens to be on the current page — with `fixed` layout
// instead, widths come only from the header row's cells below, so they stay
// put across pages regardless of how long a name or badge is.
const JOBS_COL_WIDTH = 70;
const PARTS_COL_WIDTH = 70;

type SortableField = 'name' | 'services' | 'parts';
type SortDirection = 'asc' | 'desc';

export interface StructureHierarchyTableProps {
  nodes: ServiceLocationHierarchy[];
  totalsById: Map<number, NodeTotals>;
  activeNodeId?: number;
  /** Gates the Name column's link to `/structure/:id`, matching the same
   *  permission check the "Location" field above already uses. Renders
   *  plain text (no link) when false or omitted. */
  canViewStructure?: boolean;
}

const sortRows = (
  rows: FlatHierarchyRow[],
  field: SortableField | null,
  direction: SortDirection
): FlatHierarchyRow[] => {
  if (!field) return rows;
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (field === 'name') return a.name.localeCompare(b.name) * sign;
    return (a[field] - b[field]) * sign;
  });
};

/**
 * Flat, paginated table for the Structure Hierarchy panel — the search/
 * "has jobs"/"has parts"-filtered node list, depth-first, with indentation
 * showing the hierarchy instead of nesting DOM. A plain MUI Table + the
 * shared TablePagination, not CustomDataGrid — that component's toolbar
 * (export, column selection, saved reports) always renders and would look
 * out of place in this small embedded panel section.
 */
export const StructureHierarchyTable: React.FC<StructureHierarchyTableProps> = ({
  nodes,
  totalsById,
  activeNodeId,
  canViewStructure = false,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<SortableField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sorting reorders across depths — fine here since a job's own location
  // is typically one flat level of terminals, not a deeper nested tree.
  const rows = useMemo(() => flattenHierarchy(nodes, totalsById), [nodes, totalsById]);
  const sortedRows = useMemo(
    () => sortRows(rows, sortField, sortDirection),
    [rows, sortField, sortDirection]
  );
  // Bump the current job's own terminal to the very top so it's visible on
  // page 1 without hunting through pages for it — but only while unsorted.
  // Once the user explicitly sorts a column, respect that literally rather
  // than fighting it with a pinned row. No separate "is a filter active"
  // check is needed: if search/"With" has filtered the active terminal out
  // of `rows` entirely, findIndex just returns -1 and this is a no-op —
  // exactly right, since there's nothing to pin once it's not shown at all.
  const displayRows = useMemo(() => {
    if (sortField !== null || activeNodeId == null) return sortedRows;
    const activeIndex = sortedRows.findIndex((row) => row.id === activeNodeId);
    if (activeIndex <= 0) return sortedRows;
    return [
      sortedRows[activeIndex],
      ...sortedRows.slice(0, activeIndex),
      ...sortedRows.slice(activeIndex + 1),
    ];
  }, [sortedRows, sortField, activeNodeId]);
  const pageCount = Math.max(1, Math.ceil(displayRows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = displayRows.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE
  );

  // Three-click cycle per column: ascending → descending → back to the
  // table's natural (depth-first) order — same convention as the
  // Replenishment Configs list's sortable columns.
  const handleSort = (field: SortableField) => {
    setPage(0);
    if (sortField !== field) {
      setSortField(field);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortField(null);
    }
  };

  // Same hand-rolled sort-arrow pattern as the Structures list's
  // SortableHeaderCell — a plain clickable label with the active column's
  // arrow icon shown only while it's actually sorted, no icon otherwise.
  // Left-aligned, matching that same reference component (it never
  // right-aligns its numeric columns' headers either).
  const sortableHeaderCell = (field: SortableField, label: string, width?: number) => {
    const isSorted = sortField === field;
    const ariaSortValue = isSorted
      ? sortDirection === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none';
    return (
      <TableCell
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          height: ROW_HEIGHT,
          py: 0,
          // MUI's default small-size cell padding is 16px left/right; the
          // body cells below use 8px (theme spacing 1) so a name can sit
          // close to the row's edge. Match that here — otherwise the
          // header text sits ~8px further right than the values under it.
          pl: 1,
          pr: 1,
          ...(width != null && { width }),
          color: isSorted ? theme.palette.v4.accent : 'text.secondary',
        }}
        aria-sort={ariaSortValue}
      >
        <Box
          component="button"
          type="button"
          onClick={() => handleSort(field)}
          aria-label={`Sort by ${label}, ${ariaSortValue}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 0.5,
            background: 'none',
            border: 'none',
            p: 0,
            font: 'inherit',
            fontWeight: 600,
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {label}
          {isSorted &&
            (sortDirection === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            ))}
        </Box>
      </TableCell>
    );
  };

  return (
    <Box>
      {/* Reserves a full page's worth of row height up front (header + N
          data rows) so a shorter last page doesn't shrink the table and
          shift the pagination control up. */}
      <TableContainer sx={{ minHeight: ROW_HEIGHT * (ROWS_PER_PAGE + 1) }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ height: ROW_HEIGHT }}>
              {sortableHeaderCell('name', 'Name')}
              {sortableHeaderCell('services', 'Jobs', JOBS_COL_WIDTH)}
              {sortableHeaderCell('parts', 'Parts', PARTS_COL_WIDTH)}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => {
              const isActive = activeNodeId != null && row.id === activeNodeId;
              return (
                <TableRow
                  key={row.id}
                  sx={{
                    height: ROW_HEIGHT,
                    bgcolor: isActive ? 'v4.tableSelected' : 'transparent',
                    '&:hover': { bgcolor: isActive ? 'v4.tableSelectedHover' : 'v4.tableHover' },
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: '11px',
                      fontWeight: isActive ? 700 : 400,
                      pl: 1 + row.depth * 2,
                      pr: 1,
                      height: ROW_HEIGHT,
                      // A table cell's `height` is only a minimum — a browser
                      // is still free to grow it if it thinks the content
                      // needs more room, and bold text's slightly different
                      // font metrics is exactly the kind of thing that can
                      // trigger that. Pinning both `lineHeight` and
                      // `maxHeight` to the same fixed value hard-locks the
                      // row's box regardless of font-weight, so the active
                      // (bold) row renders at the same size as every other.
                      lineHeight: `${ROW_HEIGHT}px`,
                      maxHeight: ROW_HEIGHT,
                      py: 0,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {canViewStructure ? (
                      <MuiLink component={RouterLink} to={`/structure/${row.id}`} underline="hover">
                        {row.name}
                      </MuiLink>
                    ) : (
                      row.name
                    )}
                  </TableCell>
                  <TableCell sx={{ height: ROW_HEIGHT, py: 0, pl: 1, pr: 1 }}>
                    {row.services > 0 ? (
                      <HierarchyCountBadge
                        nodeId={row.id}
                        count={row.services}
                        ownCount={row.ownServices}
                        kind="services"
                      />
                    ) : (
                      <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>–</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ height: ROW_HEIGHT, py: 0, pl: 1, pr: 1 }}>
                    {row.parts > 0 ? (
                      <HierarchyCountBadge
                        nodeId={row.id}
                        count={row.parts}
                        ownCount={row.ownParts}
                        kind="parts"
                      />
                    ) : (
                      <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>–</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {displayRows.length > ROWS_PER_PAGE && (
        <TablePagination
          page={currentPage}
          pageSize={ROWS_PER_PAGE}
          total={displayRows.length}
          onPageChange={setPage}
          onPageSizeChange={() => undefined}
        />
      )}
    </Box>
  );
};
